import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_TYPE_LABELS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
  VALID_FILE_OPTIONS,
} from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import {
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import { DocumentCategoryModal } from "./DocumentCategoryModal";
import { useManageDocumentCategoryViewModel } from "./useManageDocumentCategoryViewModel";

export function ManageDocumentCategory() {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { hasPermission } = usePermissions();

  const { classes } = useStyles();
  const theme = useTheme();
  const {
    documentCategories,
    getAllDocumentCategories,
    deleteDocumentCategory,
    createDocumentCategory,
    isLoading,
    updateDocumentCategory,
  } = useManageDocumentCategoryViewModel();

  useEffect(() => {
    getAllDocumentCategories();
  }, []);

  const hasEditPermission = hasPermission(PERMISSIONS.DOCUMENT_CATEGORY.UPDATE);

  const documentCategoryList = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 2,
    },
    {
      field: "allowedFileFormats",
      headerName: "Allowed File Formats",
      headerAlign: "left",
      align: "left",
      flex: 2,
      valueGetter: (value) => {
        return value
          ?.map(
            (format) =>
              VALID_FILE_OPTIONS.find((f) => f.value === format)?.label
          )
          ?.join(", ");
      },
    },
    {
      field: "dependentType",
      headerName: "Related To",
      headerAlign: "left",
      align: "left",
      flex: 1,
      valueGetter: (dependentType) => {
        return ENTITY_TYPE_LABELS[dependentType];
      },
    },
  ];

  const onDelete = () => {
    deleteDocumentCategory({
      id: selectedRow.id,
      modifiedOn: selectedRow.modifiedOn,
    });
    onCloseDeleteModal();
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedRow(null);
  };

  const getRowActions = (row) => {
    const actions = [];
    if (hasEditPermission) {
      actions.push({
        text: BUTTON_LABELS.EDIT,
        onClick: () => {
          setOpenAddModal(true);
          setSelectedRow(row);
        },
        renderIcon: () => (
          <MiIcon
            path={mdi[ICONS_WEB.PENCIL_OUTLINE]}
            size={1}
            color={theme.palette.green.main}
          />
        ),
      });
    }

    if (hasPermission(PERMISSIONS.DOCUMENT_CATEGORY.DELETE)) {
      actions.push({
        text: BUTTON_LABELS.DELETE,
        onClick: () => {
          setIsOpenDeleteModal(true);
          setSelectedRow(row);
        },
        disabled: row.isSystemDefined,
        popperContent: row.isSystemDefined ? (
          <Box className={classes.popper}>
            <MiIcon
              path={mdi[ICONS_WEB.ALERT_OUTLINE]}
              color={theme.palette.orange.main}
              size={2}
            />
            <Typography variant="body2">
              This Document Category is system-defined and cannot be deleted.
            </Typography>
          </Box>
        ) : null,
        renderIcon: () => (
          <MiIcon
            path={mdi[ICONS_WEB.TRASH_CAN]}
            size={1}
            color={theme.palette.red.main}
          />
        ),
      });
    }
    return actions;
  };

  const handleAddUpdateDocumentCategory = (data) => {
    if (selectedRow) {
      const newCategory = { ...selectedRow, ...data };
      updateDocumentCategory(newCategory);
    } else {
      createDocumentCategory(data);
    }
    setOpenAddModal(false);
  };

  const apiRef = useGridApiRef();

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: ["dependentType"],
      },
    },
  });

  return (
    <div className={classes.contentContainer}>
      <MiDetailPageHeader
        title="Document Categories"
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
        loading={isLoading}
        actionComponent={
          hasPermission(PERMISSIONS.DOCUMENT_CATEGORY.CREATE) && (
            <MiButton
              title="Add Document Category"
              variant={BUTTON_VARIANTS.CONTAINED}
              size="medium"
              startIcon={{
                path: mdi[ICONS_WEB.PLUS],
                iconSize: 0.8,
              }}
              onClick={() => setOpenAddModal(true)}
              data-testid="Add Document Category"
            />
          )
        }
      />
      <MiBox className={classes.container}>
        <MiList
          columns={documentCategoryList}
          data={documentCategories}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          getRowActions={getRowActions}
          data-testid="document-categories-table"
          apiRef={apiRef}
          initialState={initialState}
          columnVisibilityModel={{ dependentType: false }}
          defaultGroupingExpansionDepth={-1}
          groupingColDef={{
            valueGetter: (_, row) => {
              return ENTITY_TYPE_LABELS[row.groupingKey];
            },
          }}
        />
      </MiBox>
      {openAddModal && (
        <DocumentCategoryModal
          onModalClose={() => {
            setOpenAddModal(false);
            setSelectedRow(null);
          }}
          open={openAddModal}
          onSubmit={(data) => handleAddUpdateDocumentCategory(data)}
          loading={isLoading}
          editItem={selectedRow}
          documentCategories={documentCategories}
        />
      )}

      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete: ${selectedRow?.name}?`}
        open={isOpenDeleteModal}
        handlePositive={onDelete}
        handleNegative={onCloseDeleteModal}
        className={classes.modal}
        textPositive={BUTTON_LABELS.DELETE}
      />
    </div>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    height: "inherit",
    overflowY: "scroll",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  popper: {
    padding: theme.spacing(2),
    maxWidth: 240,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.orange.light,
    border: `1px solid ${theme.palette.orange.dark}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));
